/**
 * MobileMenu.tsx
 * --------------------------------------------------------------------------
 * Sheet fullscreen lateral (desde la derecha) para navegacion en mobile.
 *
 * Comportamiento:
 *  - Animacion slide-in 200 ms con framer-motion. Curva ease-out de marca.
 *  - Respeta `prefers-reduced-motion`: cuando se prefiere reducir movimiento
 *    la animacion se neutraliza (solo opacity, sin desplazamiento).
 *  - Cierra con boton X, tecla ESC y click en el backdrop.
 *  - Bloquea el scroll del body mientras esta abierto.
 *  - Lista vertical de links grandes (text-h4), CTA "Reservar cita"
 *    prominente al final + acceso directo a /cuenta.
 *
 * Solo tokens del sistema. Sin paleta legacy ni hex literales.
 */
import { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { Variants } from 'framer-motion';
import {
  CalendarDays,
  ChevronRight,
  Heart,
  Home,
  Image as ImageIcon,
  MessageCircle,
  Moon,
  Search,
  ShoppingBag,
  Sparkles,
  User,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Routes } from '../../lib/routes';
import { cn } from '../../lib/cn';
import { LotoIcon } from '../icons';
import { CartIconBadge } from './CartIconBadge';
import type { NavItem } from './Navbar';

/**
 * Mapa href → ícono para la lista de navegación del panel móvil.
 * Si una ruta no tiene mapeo explícito, cae a `Sparkles` (neutro y dentro
 * de la familia visual de la marca).
 */
const NAV_ICON_BY_HREF: Record<string, LucideIcon> = {
  [Routes.home]: Home,
  [Routes.therapies]: Sparkles,
  [Routes.shop]: ShoppingBag,
  [Routes.gallery]: ImageIcon,
  [Routes.about]: Heart,
  [Routes.events]: CalendarDays,
  [Routes.contact]: MessageCircle,
};

// Tipos exportados ----------------------------------------------------------

export interface MobileMenuProps {
  /** Controla la visibilidad del sheet. */
  isOpen: boolean;
  /** Callback que la implementacion padre dispara al cerrar. */
  onClose: () => void;
  /**
   * Callback opcional para abrir el `GlobalSearchModal` desde el sheet móvil.
   * Si no se provee, el botón de búsqueda queda como no-op (solo cierra el
   * sheet) — útil para tests o entornos donde la búsqueda global no aplica.
   */
  onOpenSearch?: () => void;
  /**
   * Callback de "Reservar cita": abre la reserva de la Orientación gratuita
   * (el `Navbar` monta el modal). Si no se provee, va a `/terapias`.
   */
  onReservar?: () => void;
  /** Items de navegacion. Importados del mismo set canonico del Navbar. */
  items: NavItem[];
}

// Variantes Framer Motion ---------------------------------------------------
// Curva y duraciones alineadas con docs/DESIGN-SYSTEM.md §6.

const backdropVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] as const } },
  exit: { opacity: 0, transition: { duration: 0.15, ease: [0.22, 1, 0.36, 1] as const } },
};

const panelVariants: Variants = {
  // Slide-down desde arriba — coherente con un overlay full-screen místico
  // que aterriza sobre el contenido (vs. el slide-from-right anterior).
  hidden: { y: '-4%', opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] as const } },
  exit: { y: '-4%', opacity: 0, transition: { duration: 0.16, ease: [0.22, 1, 0.36, 1] as const } },
};

// Componente ----------------------------------------------------------------

// Selector de elementos enfocables dentro del panel (foco trap minimo).
// Mismos criterios que el Modal y Drawer del DS.
const FOCUSABLE_SELECTOR =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

export function MobileMenu({ isOpen, onClose, onOpenSearch, onReservar, items }: MobileMenuProps) {
  const location = useLocation();
  const navigate = useNavigate();
  // Si el usuario prefiere menos movimiento, devolvemos variantes planas.
  const reduceMotion = useReducedMotion();
  // Refs para focus management y trap.
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Búsqueda — handoff coordinado al GlobalSearchModal global.
  // Cerramos el sheet primero para que el modal no aparezca por debajo, y
  // luego abrimos el palette. El padre (`Navbar`) gestiona el `isSearchOpen`
  // y se encarga de montar/desmontar el modal con sus propias queries.
  const handleSearchClick = () => {
    onClose();
    onOpenSearch?.();
  };

  // "Reservar cita": mismo handoff que la búsqueda (cerrar el sheet y abrir
  // la reserva de la Orientación que monta el `Navbar`).
  const handleReservarClick = () => {
    onClose();
    if (onReservar) onReservar();
    else navigate(Routes.therapies);
  };

  // ESC cierra el sheet (incluso si el foco no esta dentro).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Bloqueamos el scroll del body cuando el sheet esta abierto.
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // F8.2 — Focus management:
  //  - Al abrir: guardamos el elemento que tenía foco, movemos foco al botón
  //    Cerrar (primer foco lógico del panel).
  //  - Al cerrar: restauramos foco al trigger original (suele ser el hamburger
  //    del Navbar, así que el usuario teclado no pierde el contexto).
  useEffect(() => {
    if (!isOpen) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    // requestAnimationFrame para que el panel ya esté montado/animado.
    const id = requestAnimationFrame(() => {
      closeBtnRef.current?.focus({ preventScroll: true });
    });
    return () => {
      cancelAnimationFrame(id);
      previouslyFocused.current?.focus?.();
    };
  }, [isOpen]);

  // F8.2 — Focus trap minimal: Tab cicla dentro del panel.
  useEffect(() => {
    if (!isOpen) return;
    const onTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !panel.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !panel.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onTab);
    return () => window.removeEventListener('keydown', onTab);
  }, [isOpen]);

  // Variantes finales segun reduced-motion.
  const safeBackdrop = reduceMotion
    ? ({
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0 } },
        exit: { opacity: 0, transition: { duration: 0 } },
      } satisfies Variants)
    : backdropVariants;
  const safePanel = reduceMotion
    ? ({
        hidden: { opacity: 0, y: 0 },
        visible: { opacity: 1, y: 0, transition: { duration: 0 } },
        exit: { opacity: 0, y: 0, transition: { duration: 0 } },
      } satisfies Variants)
    : panelVariants;

  const isActiveItem = (href: string) => {
    if (href === Routes.home) return location.pathname === Routes.home;
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        // Wrapper fijo a pantalla completa. md:hidden — el menú sólo aplica
        // para viewport mobile. `data-dark-bg="true"` mantiene el contraste
        // del Navbar mientras el panel cubre la zona superior.
        <div className="fixed inset-0 z-overlay md:hidden" id="mobile-menu" data-dark-bg="true">
          {/* Backdrop — violeta translúcido + blur (paleta `primary-900/45`
              con backdrop-blur 8 px, matchea el spec del lead). El click cierra
              y el `<div>` no entra al focus trap por no ser tabbable. */}
          <motion.div
            aria-hidden="true"
            onClick={onClose}
            className="absolute inset-0 bg-primary-900/45 backdrop-blur-md cursor-pointer"
            variants={safeBackdrop}
            initial="hidden"
            animate="visible"
            exit="exit"
          />

          {/* Panel principal — full-screen con margen mínimo lateral e inferior
              para que se vean las esquinas inferiores redondeadas sobre el
              backdrop. Gradiente violeta noche, borde dorado fino, sombra
              profunda. Capas decorativas dentro vía `MysticDecorations`. */}
          <motion.aside
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Menú principal"
            aria-labelledby="mobile-menu-title"
            className={cn(
              'absolute inset-x-2 top-0 bottom-3',
              'overflow-hidden flex flex-col',
              // Fondo: gradiente violeta noche → vívido (radial dorado arriba
              // izquierda agregado vía `style` para usar la misma curva exacta
              // del spec — Tailwind no expone radial-gradient con stops crudos).
              'bg-gradient-to-br from-primary-900 via-primary-800 to-primary-vivid',
              // Borde dorado fino + esquinas inferiores redondeadas grandes.
              'border border-gold-500/35 border-t-0 rounded-b-3xl',
              'shadow-elev3 text-cream-100',
            )}
            style={{
              // Velo radial dorado superior izquierdo, tomado del spec del
              // lead (rgba(214,169,58,0.12) hasta 28% del radio).
              backgroundImage:
                'radial-gradient(circle at 20% 10%, rgba(214,169,58,0.12), transparent 28%), linear-gradient(160deg, var(--color-primary-900) 0%, var(--color-primary-800) 55%, var(--color-primary-vivid) 100%)',
            }}
            variants={safePanel}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Capa decorativa — sparkles, puntos dorados, luna, auroras. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 overflow-hidden"
            >
              <div className="absolute -left-24 -top-24 h-64 w-64 rounded-pill bg-primary-500/25 blur-3xl" />
              <div className="absolute -right-32 -bottom-32 h-80 w-80 rounded-pill bg-gold-500/15 blur-3xl" />
              <Moon
                className="absolute right-6 top-24 h-7 w-7 text-gold-soft/40"
                strokeWidth={1.25}
              />
              <Sparkles
                className="absolute left-[12%] top-32 h-3 w-3 text-gold-500/55"
                strokeWidth={1.5}
              />
              {/* Anclado al borde derecho (`right-4`, no `right-[14%]`) para
                  que no caiga sobre la letra 't' de "Eventos" — el quinto
                  item de la lista vive cerca del 55 % vertical del panel. */}
              <Sparkles
                className="absolute right-4 top-[55%] h-4 w-4 text-gold-500/45"
                strokeWidth={1.5}
              />
              <Sparkles
                className="absolute left-[20%] bottom-40 h-3 w-3 text-gold-soft/60"
                strokeWidth={1.5}
              />
              <span className="absolute top-[40%] left-[6%] h-1 w-1 rounded-pill bg-gold-soft/70" />
              <span className="absolute bottom-1/3 right-[10%] h-1.5 w-1.5 rounded-pill bg-gold-500/55" />
              <span className="absolute top-1/4 right-[28%] h-1 w-1 rounded-pill bg-gold-soft/60" />
            </div>

            {/* ----- Encabezado: logo centrado + botón cerrar ----- */}
            <div className="relative grid grid-cols-[44px_1fr_44px] items-center px-4 py-5 border-b border-gold-500/20">
              {/* Spacer izquierdo para balancear el botón X y mantener el logo
                  visualmente centrado. */}
              <span aria-hidden="true" className="h-11 w-11" />

              {/* Logo Ceciluz centrado (loto en píldora dorada + texto). */}
              <div className="flex flex-col items-center gap-1">
                <span
                  aria-hidden="true"
                  className={cn(
                    'inline-flex h-11 w-11 items-center justify-center rounded-pill',
                    'border border-gold-500/50 bg-cream-50/10 text-gold-soft',
                    'shadow-elev1',
                  )}
                >
                  <LotoIcon size={22} strokeWidth={1.5} />
                </span>
                <h2
                  id="mobile-menu-title"
                  className="font-display text-h4 text-cream-100 leading-none"
                >
                  Ceciluz Arte
                </h2>
              </div>

              {/* Botón cerrar — touch target 44×44 dorado suave. */}
              <button
                ref={closeBtnRef}
                type="button"
                onClick={onClose}
                aria-label="Cerrar menú"
                className={cn(
                  'inline-flex items-center justify-center justify-self-end',
                  'h-11 w-11 rounded-pill',
                  'border border-gold-500/40 text-gold-soft',
                  'hover:bg-cream-50/10 hover:border-gold-soft hover:text-cream-100',
                  'transition-colors duration-2 ease-out',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-primary-900',
                )}
              >
                <X className="h-5 w-5" aria-hidden="true" strokeWidth={1.5} />
              </button>
            </div>

            {/* ----- Lista vertical de navegación con íconos dorados ----- */}
            <nav
              aria-label="Navegación principal"
              className="relative flex-1 overflow-y-auto px-4 py-3"
            >
              <ul role="list" className="divide-y divide-gold-500/15">
                {items.map((item) => {
                  const Icon = NAV_ICON_BY_HREF[item.href] ?? Sparkles;
                  const active = isActiveItem(item.href);
                  return (
                    <li key={item.href}>
                      <Link
                        to={item.href}
                        aria-current={active ? 'page' : undefined}
                        onClick={onClose}
                        className={cn(
                          'group flex items-center gap-3 px-1 py-3.5',
                          'font-body text-body transition-colors duration-2 ease-out',
                          active ? 'text-gold-soft' : 'text-cream-100 hover:text-gold-soft',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-primary-900 rounded-md',
                        )}
                      >
                        <span
                          aria-hidden="true"
                          className={cn(
                            'inline-flex h-5 w-5 shrink-0 items-center justify-center',
                            active ? 'text-gold-soft' : 'text-gold-500',
                            'transition-colors duration-2 ease-out group-hover:text-gold-soft',
                          )}
                        >
                          <Icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
                        </span>
                        <span className="flex-1">{item.label}</span>
                        <ChevronRight
                          className={cn(
                            'h-4 w-4 shrink-0 text-gold-500/60',
                            'transition-transform duration-2 ease-out',
                            'group-hover:translate-x-0.5 group-hover:text-gold-soft',
                          )}
                          strokeWidth={1.5}
                          aria-hidden="true"
                        />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* ----- Barra inferior: búsqueda, cuenta, bolsa, reservar cita ----- */}
            <div
              className={cn(
                'relative px-4 py-4',
                'border-t border-gold-500/25',
                // Fondo levemente más oscuro para diferenciar la franja del CTA.
                'bg-primary-900/40',
              )}
            >
              <div className="flex items-center gap-2.5">
                {/* Búsqueda — emite el mismo toast "Próximamente" que el
                    Navbar para mantener la consistencia global. */}
                <button
                  type="button"
                  onClick={handleSearchClick}
                  aria-label="Buscar"
                  className={cn(
                    'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-pill',
                    'border border-gold-500/40 text-gold-soft',
                    'hover:bg-cream-50/10 hover:border-gold-soft hover:text-cream-100',
                    'transition-colors duration-2 ease-out',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-primary-900',
                  )}
                >
                  <Search className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
                </button>

                {/* Cuenta — link directo a /cuenta. */}
                <Link
                  to={Routes.account}
                  onClick={onClose}
                  aria-label="Mi cuenta"
                  className={cn(
                    'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-pill',
                    'border border-gold-500/40 text-gold-soft',
                    'hover:bg-cream-50/10 hover:border-gold-soft hover:text-cream-100',
                    'transition-colors duration-2 ease-out',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-primary-900',
                  )}
                >
                  <User className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
                </Link>

                {/* Bolsa con badge — reutiliza `CartIconBadge` con tono dark-bg
                    para que el contador real del carrito se respete sin
                    duplicar lógica. Le ponemos borde dorado para casarlo con
                    los otros action pills. */}
                <CartIconBadge
                  tone="dark-bg"
                  className="shrink-0 border border-gold-500/40 hover:border-gold-soft"
                />

                {/* CTA Reservar cita — pill ocupa el ancho restante. Abre la
                    reserva de la Orientación gratuita en la página actual. */}
                <button
                  type="button"
                  onClick={handleReservarClick}
                  className={cn(
                    'inline-flex flex-1 items-center justify-center gap-2',
                    'h-11 rounded-pill px-4',
                    'border border-gold-500/60 bg-cream-50/5',
                    'font-body text-button uppercase tracking-[0.04em] text-cream-100',
                    'transition-all duration-2 ease-out',
                    'hover:border-gold-soft hover:bg-cream-50/10',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-primary-900',
                  )}
                >
                  <Sparkles
                    className="h-4 w-4 text-gold-soft"
                    strokeWidth={1.5}
                    aria-hidden="true"
                  />
                  <span>Reservar cita</span>
                </button>
              </div>
            </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
